import React, { useCallback, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { rem, SettingsItemCard } from 'design-system';
import Button from 'design-system/Button';
import { EditableAvatar } from 'design-system/EditableAvatar';
import Text from 'design-system/Text';
import { CardView } from 'design-system/CardView';
import { IconDiamondNew } from 'design-system/icons/IconDiamondNew';
import { IconEmma } from 'design-system/icons/IconEmma';
import { IconScan } from 'design-system/icons/IconScan';
import { Avatar } from 'design-system/avatar';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { useAppSelector } from 'store/hooks';
import { useProfileImagePicker } from 'features/settings/hooks';
import useColors from 'hooks/useColors';
import { selectDefaultCurrency, selectIsGBUser, selectUser, } from 'reducers/selectors';
import { mapIOSPermissions } from 'utils/permissions';
import { Camera } from 'utils/packages/Camera';
import { tierThemes } from 'features/premium/benefits';
import { isPremiumTier } from 'features/premium/entities';
import { selectUserTier } from 'features/premium/selectors/tier';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Quests from 'features/feed/components/quests/Quests';
import { PermissionStatus } from 'features/pay/types/types';
import YearlyReportBanner from 'features/feed/components/reports/YearlyReportBanner';
import Links from 'utils/links';
import { Amplitude, formatTryFor0 } from 'utils';
import { logAnalyticsEvent } from 'utils/packages/rnFirebase';
import { YEARLY_REPORT_FEED } from 'actions/deviceSettingTypes';
import { DEFAULT_UPGRADE_TEXT, selectTryFor0Text, } from 'features/premium/selectors';
import { isWeb } from '../../../constants';
import LearnCard from './LearnCard';
const strings = {
    upgrade: 'Upgrade',
    ultimate: 'Ultimate',
    inviteFriends: 'Invite and get ',
    invite: 'Invite',
    planDescription: 'Your plan and benefits',
};
const AVATAR_SIZE = rem(56);
export const AccountSettingsHeader = React.memo(({ onLayoutQuests, onLayoutLearn }) => {
    const colors = useColors();
    const user = useAppSelector(selectUser);
    const isGBUser = useAppSelector(selectIsGBUser);
    const navigation = useMainStackNavigation();
    const { showProfileImagePicker } = useProfileImagePicker();
    const userTier = useAppSelector(selectUserTier);
    const isPremium = isPremiumTier(userTier);
    const handlePlanCardPress = useCallback(() => {
        navigation.navigateDeprecated('PlanOverview');
    }, []);
    const handleUpgradeBtnPress = useCallback(() => {
        if (isWeb) {
            UpgradeWebModal.show(userTier);
        }
        else {
            navigation.navigateDeprecated('EmmaProModal', {});
        }
    }, [userTier]);
    const emmaIconColorProps = useMemo(() => {
        if (!isPremium) {
            return { colorKey: 'secondary' };
        }
        const shortName = tierThemes[userTier].shortName.toLowerCase();
        const gradientKey = ['ultimate', 'pro', 'plus'].includes(shortName)
            ? shortName
            : 'ultimate';
        return { gradientKey };
    }, [isPremium, userTier]);
    const hasCameraPermission = useRef(false);
    const openQRCode = useCallback(() => {
        const permission = hasCameraPermission.current ||
            mapIOSPermissions(Camera.getCameraPermissionStatus()) ===
                PermissionStatus.allowed;
        hasCameraPermission.current = permission;
        navigation.navigateDeprecated('QRCodeScreen', {
            hasCameraPermission: permission,
            fromAccountSettings: true,
        });
    }, []);
    const yearlyReportCoverInfo = useAppSelector((store) => store.feed.feed.cards.yearlyReportCoverInfo);
    const didDismissYearlyReportFeed = useAppSelector((store) => store.deviceSettings[YEARLY_REPORT_FEED]);
    const openReport = useCallback((data) => {
        if (data.webUrl) {
            Links.openLink(data.webUrl);
        }
        Amplitude.logEvent('Report.Open', { type: data.type });
        logAnalyticsEvent('Open_Report', { type: data.type });
    }, []);
    const tryFor0Text = useAppSelector(selectTryFor0Text);
    const userCurrency = useAppSelector(selectDefaultCurrency);
    return (<>
        <CardView style={styles.container} noMarginTop noMarginBottom>
          {isWeb ? (<Avatar primary size={AVATAR_SIZE} large name={user.firstName} lastName={user.lastName} imageUrl={user.userImage?.url} color={colors.background.brandLight} fontColor={colors.text.textOnBrand} borderWidth={2}/>) : (<EditableAvatar name={user.firstName} lastName={user.lastName} onPress={showProfileImagePicker} size={AVATAR_SIZE} imageUrl={user.userImage?.url}/>)}
          <View style={styles.userInfoContainer}>
            <Text Text-24 numberOfLines={1}>
              {`${user.firstName} ${user.lastName}`}
            </Text>
            {isWeb ? (<View style={styles.userHandleContainer}>
                <Text TextThin-14 Secondary>
                  @{user.urlHandle}
                </Text>
              </View>) : (<TouchableBounce style={styles.userHandleContainer} onPress={openQRCode} disabled={!isGBUser}>
                <Text TextThin-14 Secondary>
                  @{user.urlHandle}
                </Text>
                {isGBUser ? (<IconScan height={16} width={16} color={colors.elements.secondary}/>) : null}
              </TouchableBounce>)}
          </View>
          {!isPremium && (<Button brandReversed square leftIcon={<IconDiamondNew color={colors.text.textOnBrandLight} width={rem(17)} height={rem(17)}/>} title={tryFor0Text && tryFor0Text !== DEFAULT_UPGRADE_TEXT
                ? formatTryFor0(userCurrency, 'Upgrade')
                : strings.upgrade} containerStyle={styles.upgradeButton} onPress={handleUpgradeBtnPress}/>)}
        </CardView>
        {yearlyReportCoverInfo && didDismissYearlyReportFeed ? (<YearlyReportBanner data={yearlyReportCoverInfo} openReport={openReport} containerStyle={styles.yearlyReportBanner}/>) : null}
        <View style={styles.cardButtonsContainer} onLayout={onLayoutLearn}>
          <SettingsItemCard icon={<IconEmma {...emmaIconColorProps} height={24} width={24}/>} title={tierThemes[userTier].name} description={strings.planDescription} onPress={handlePlanCardPress}/>
          <LearnCard />
        </View>
        <View onLayout={onLayoutQuests}>
          <Quests key="Quests"/>
        </View>
      </>);
});
const styles = StyleSheet.create({
    container: {
        padding: rem(16),
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: rem(16),
    },
    userInfoContainer: {
        flex: 1,
        alignItems: 'center',
    },
    userHandleContainer: {
        marginTop: rem(4),
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
    },
    upgradeButton: {
        height: rem(37),
    },
    cardButtonsContainer: {
        flexDirection: 'row',
        gap: rem(16),
        marginHorizontal: rem(16),
        marginVertical: rem(16),
    },
    yearlyReportBanner: {
        marginBottom: 0,
        marginTop: rem(16),
    },
});
